import { useState, useEffect } from "react";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { toast } from "react-toastify";
import {
  PageContainer, StatCard, PremiumCard, LoadingState, EmptyState, Field,
} from "components/ui";
import ExportButtons from "components/ui/ExportButtons";

const IconCollection = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
  </svg>
);
const IconCart = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </svg>
);
const IconChart = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const ProfitLoss = () => {
  const axiosPrivate = useAxiosPrivate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState(`${new Date().getFullYear()}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axiosPrivate.get(`/api/accountant/income-statement?start=${startDate}&end=${endDate}`);
      setData(res.data);
    } catch (_) {
      toast.error("Failed to load P&L");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [startDate, endDate]);

  return (
    <PageContainer>
      <PremiumCard
        title="Profit & loss"
        subtitle="Income statement for the selected period"
        actions={
          <div
            style={{
              display: "inline-flex",
              gap: 12,
              alignItems: "flex-end",
              flexWrap: "wrap",
            }}
          >
            <div style={{ minWidth: 150 }}>
              <Field
                label="From"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            <div style={{ minWidth: 150 }}>
              <Field
                label="To"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            <ExportButtons
              path="income-statement"
              params={{
                start: startDate,
                end: endDate,
              }}
              filename={`income-statement-${startDate}_${endDate}`}
            />
          </div>
        }
      >
        {loading ? (
          <LoadingState label="Computing P&L…" />
        ) : !data ? (
          <EmptyState title="No data available" description="Try a different date range." />
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 16,
                marginBottom: 24,
              }}
            >
              <StatCard
                label="Revenue" value={`${Number(data.revenue).toFixed(2)} TND`}
                icon={<IconCollection />}
                accent={{ bg: "var(--success-bg)", fg: "var(--success)" }}
              />
              <StatCard
                label="Expenses" value={`${Number(data.expenses).toFixed(2)} TND`}
                icon={<IconCart />}
                accent={{ bg: "var(--danger-bg)", fg: "var(--danger)" }}
              />
              <StatCard
                label="Net income" value={`${Number(data.netIncome).toFixed(2)} TND`}
                icon={<IconChart />}
                accent={data.netIncome >= 0
                  ? { bg: "var(--success-bg)", fg: "var(--success)" }
                  : { bg: "var(--danger-bg)", fg: "var(--danger)" }}
              />
            </div>

            <div
              style={{
                border: "1px solid var(--border-subtle)",
                borderRadius: 14,
                padding: 20,
                background: "var(--surface-muted)",
              }}
            >
              <p className="t-body-sm t-muted" style={{ marginBottom: 12 }}>
                Period: {data.start} → {data.end}
              </p>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0" }}>
                <span>Total revenue</span>
                <strong className="t-success t-mono">{Number(data.revenue).toFixed(2)} TND</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                <span>Total expenses</span>
                <strong className="t-danger t-mono">{Number(data.expenses).toFixed(2)} TND</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0 0", fontWeight: 700 }}>
                <span>Net income</span>
                <span className={`${data.netIncome >= 0 ? "t-success" : "t-danger"} t-mono`}>
                  {Number(data.netIncome).toFixed(2)} TND
                </span>
              </div>
            </div>
          </>
        )}
      </PremiumCard>
    </PageContainer>
  );
};

export default ProfitLoss;